import { useId, useState } from "react";
import type { CSSProperties } from "react";
import type { Payment } from "../../types";

// Local rather than store state: which tab is open is a fact about this glance
// at the page, not about the user's finances.
type FinTab = "charts" | "overview" | "flow" | "debt" | "plan";
import { useHudStore } from "../../store/hudStore";
import { EXP_CATS, ACC_META, CUR_MONTHS, curMonth, peso } from "../../data/seed";
import { financeDateIso, isWithinFinanceDateRange } from "../../lib/financeDates";
import { availableCash, cashAfterDebits } from "../../../../../shared/financeCash.ts";
import { buildCashForecast } from "../../lib/financeForecast";
import { fill } from "../ui";
import { Button, Field, Tabs } from "../../design-system/components";
import "./FinanceModule.css";
import FinanceAnalytics from "./FinanceAnalytics";
import { isReceivableDebt, isReceivablePayment } from "../../lib/financeAnalytics";

// Finances module: budget, ledger, debt snowball, payments
// (app.html fchip + financeModule, lines 1094–1169).

const cardBox: CSSProperties = { minWidth: 0, background: "var(--card)", border: "1px solid var(--line)", borderRadius: 10, padding: 20 };

// New API rows carry their full ISO date. The legacy API used display dates;
// keep that fallback readable without pulling a prior year's payment into this
// month's total. Only a genuinely yearless legacy date borrows the current year.
const paymentIso = (p: Payment, fallbackYear: number): string => {
  const raw = String(p.iso || p.date || "").trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  const display = iso ? null : /^([A-Za-z]{3})[a-z]*\s+(\d{1,2})(?:,?\s+(\d{4}))?$/.exec(raw);
  const year = iso ? Number(iso[1]) : display?.[3] ? Number(display[3]) : fallbackYear;
  const month = iso ? Number(iso[2]) : display ? CUR_MONTHS.findIndex((m) => m.toLowerCase() === display[1]!.toLowerCase()) + 1 : 0;
  const day = iso ? Number(iso[3]) : Number(display?.[2]);
  if (!month || !day || !Number.isFinite(year)) return "";
  const candidate = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  const date = new Date(Date.UTC(year, month - 1, day));
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === candidate ? candidate : "";
};
const completeSplit = (p: Payment): boolean =>
  Number.isFinite(p.amount) && p.amount > 0 &&
  typeof p.principal === "number" && Number.isFinite(p.principal) && p.principal >= 0 &&
  typeof p.interest === "number" && Number.isFinite(p.interest) && p.interest >= 0 &&
  Math.round((p.principal + p.interest) * 100) === Math.round(p.amount * 100);
const paymentPeso = (n: number): string => "₱" + n.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Account/category selection uses the same buttons as the rest of the HUD.
function Fchip({ label, on, icon, onSelect }: { label: string; on: boolean; icon?: string; onSelect: () => void }) {
  return <Button size="compact" variant={on ? "primary" : "secondary"} aria-pressed={on} icon={icon} onClick={onSelect}>{label}</Button>;
}

export default function FinanceModule() {
  const st = useHudStore();
  const sectionsId = `finance-sections-${useId()}`;
  const ledgerId = `finance-ledger-${useId()}`;
  const closedLoansId = `finance-closed-loans-${useId()}`;
  const paymentHistoryId = `finance-payment-history-${useId()}`;
  const [tab, setTab] = useState<FinTab>(() => {
    const view = new URLSearchParams(location.search).get("view");
    return (["charts", "overview", "flow", "debt", "plan"] as const).find((key) => key === view) || "charts";
  });
  // Which page of the ledger is open. Local glance-state; reset to 0 when the
  // tab (All / Income / Spending) changes, since that swaps the underlying list.
  const [ledgerP, setLedgerP] = useState(0);
  const [paymentP, setPaymentP] = useState(0);
  const [catFilter, setCatFilter] = useState<string | null>(null); // active category-pill filter; null = off
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const dateRangeActive = !!(dateStart || dateEnd);
  const dateRangeInvalid = !!(dateStart && dateEnd && dateStart > dateEnd);
  const inDateRange = (date: string) => isWithinFinanceDateRange(date, dateStart, dateEnd);
  // Standing costs disclosure. Collapsed by default: the arrangements change
  // rarely, so the roll-up line is the glanceable fact and the rows are
  // reference for the moment one of them is actually being questioned.
  const [standingOpen, setStandingOpen] = useState(false);
  const [closedOpen, setClosedOpen] = useState(false);
  // A settled instalment belongs to history, even when its old monthly amount
  // or due date remains on the record. A cleared card or revolving BNPL line
  // with a credit limit remains open; a finite BNPL plan has a historical start.
  const isClosedLoan = (d: typeof st.debts[number]) => {
    const revolving = d.kind === "card" || (d.kind === "bnpl" && !d.start && (d.limit ?? 0) > 0);
    return !revolving && d.balance <= 0;
  };
  const payableDebts = st.debts.filter((d) => !isReceivableDebt(d));
  const receivables = st.debts.filter((d) => isReceivableDebt(d));
  const activeReceivables = receivables.filter((d) => d.balance > 0);
  const closedReceivables = receivables.filter((d) => d.balance <= 0);
  const totalReceivable = activeReceivables.reduce((total, d) => total + d.balance, 0);
  const closedDebts = payableDebts.filter(isClosedLoan);
  const activeDebts = payableDebts.filter((d) => !isClosedLoan(d));
  // Money leaves by more routes than a bank account. Most subscriptions here are
  // charged to a card, and a card is a `debt` rather than an `account`, so
  // resolving only against accounts printed "None" against every one of them.
  // Falls through to the shared credit line, then to a prettified id, so an
  // unrecognised source still reads as a name instead of as missing data.
  const accName = (id: string): string => {
    if (!id) return "None";
    const acc = st.accounts.find((a) => a.id === id);
    if (acc) return acc.name;
    const d = st.debts.find((x) => x.id === id);
    if (d) return d.name;
    const byFacility = st.debts.find((x) => x.facility === id);
    if (byFacility) return byFacility.lender || id;
    return id.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };
  const cashOnHand = availableCash(st.accounts);
  const onHand = peso(cashOnHand);
  const activeMonth = curMonth();
  const activeMonthIso = new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 7); // Manila
  const paymentHistory = st.payments.filter((p) => !isReceivablePayment(p, st.debts)).map((p, i) => ({
    ...p,
    historyKey: p.id || `${p.debtId || p.name}:${p.date}:${p.amount}:${i}`,
    historyIso: paymentIso(p, Number(activeMonthIso.slice(0, 4))),
  })).sort((a, b) => a.historyIso && b.historyIso ? b.historyIso.localeCompare(a.historyIso) : a.historyIso ? -1 : b.historyIso ? 1 : 0);
  const PAYMENT_PAGE = 8;
  const paymentPages = Math.max(1, Math.ceil(paymentHistory.length / PAYMENT_PAGE));
  const paymentPage = Math.min(paymentP, paymentPages - 1);
  const paymentRows = paymentHistory.slice(paymentPage * PAYMENT_PAGE, (paymentPage + 1) * PAYMENT_PAGE);
  const monthIn = st.incomes.filter((i) => i.month === activeMonth).reduce((t, i) => t + i.amount, 0);
  const monthSpend = st.expenses.filter((e) => e.month === activeMonth).reduce((t, e) => t + e.amount, 0);
  const monthDebt = paymentHistory.filter((p) => p.historyIso.slice(0, 7) === activeMonthIso).reduce((t, p) => t + p.amount, 0);
  const net = monthIn - (monthSpend + monthDebt);
  // Period totals cover every matching expense, not just the current page.
  // Category chips use the same period; transfers and repayments never count.
  const rangeExpenses = st.expenses.filter((e) => inDateRange(e.date));
  const totalSpend = rangeExpenses.reduce((t, e) => t + e.amount, 0);
  const spendCats = EXP_CATS.map((c) => ({ c, sum: rangeExpenses.filter((e) => e.cat === c.key).reduce((t, e) => t + e.amount, 0) })).filter((x) => x.sum > 0 || x.c.key === catFilter);
  const selectedExpenses = catFilter ? rangeExpenses.filter((e) => e.cat === catFilter) : rangeExpenses;
  const selectedSpend = selectedExpenses.reduce((t, e) => t + e.amount, 0);
  const selectedCategory = EXP_CATS.find((c) => c.key === catFilter);

  // By key, not by index. This was EXP_CATS[5], which meant the fallback
  // category was whatever happened to sit sixth, and adding a category above
  // "other" silently made the fallback something else.
  const OTHER = EXP_CATS.find((c) => c.key === "other") ?? { key: "other", label: "Other", fa: "fa-solid fa-ellipsis", color: "var(--mut)" };
  const inc = st.incomes.map((i) => ({ date: i.date, label: i.source, account: accName(i.account), amount: "+" + peso(i.amount), color: "var(--green-ink)", fa: "fa-solid fa-arrow-down", dot: "var(--green)", cat: "" }));
  const exp = st.expenses.map((e) => {
    const c = EXP_CATS.find((x) => x.key === e.cat) || OTHER;
    return { date: e.date, label: e.note, account: accName(e.account) + " · " + c.label, amount: "−" + peso(e.amount), color: "var(--orange-ink)", fa: c.fa, dot: c.color, cat: e.cat };
  });
  // Transfers appear under "All activity" and nowhere else. They are neither
  // earned nor spent, so putting them in the Income or Spending tab would make
  // a month report money it never made or never lost. They are excluded from
  // the category split and the in/out totals for the same reason. Shown at all
  // because a balance that jumps with no row against it reads as an error.
  const mv = st.transfers.map((t) => ({
    date: t.date,
    label: t.note || (accName(t.from) + " to " + accName(t.to)),
    account: accName(t.from) + " → " + accName(t.to),
    amount: peso(t.amount),
    color: "var(--sub)",
    fa: "fa-solid fa-right-left",
    dot: "var(--mut)",
    cat: "",
  }));
  // Debt payments as movements. They debit an account with no ledger row of their
  // own, so they belong in "All activity" beside transfers - money out, but not
  // spending (they pay down a liability), so they stay out of the Spending tab.
  const dp = st.debtPayments.filter((p) => !isReceivablePayment(p, st.debts)).map((p) => ({
    date: p.date,
    label: p.name,
    account: accName(p.account) + " · debt",
    amount: "−" + peso(p.amount),
    color: "var(--orange-ink)",
    fa: "fa-solid fa-hand-holding-dollar",
    dot: p.color || "var(--orange)",
    cat: "",
  }));
  // A category pill acts as a filter: while one is active it overrides the
  // All/Income/Spending tab and shows just that category's spending.
  const chosen = catFilter
    ? exp.filter((r) => r.cat === catFilter)
    : st.ledger === "in" ? inc : st.ledger === "out" ? exp : exp.concat(inc, mv, dp);
  // Filter before sorting and paging. Preserve years so a range across New Year
  // includes and orders both years correctly; unparseable dates sort last.
  const LEDGER_PAGE = 8;
  const sortedLedger = chosen.filter((r) => inDateRange(r.date)).sort((a, b) =>
    (financeDateIso(b.date) ?? "").localeCompare(financeDateIso(a.date) ?? ""));
  const ledgerPages = Math.max(1, Math.ceil(sortedLedger.length / LEDGER_PAGE));
  const ledgerPage = Math.min(ledgerP, ledgerPages - 1); // clamp: a tab switch can shrink the list under the current page
  const ledgerRows = sortedLedger.slice(ledgerPage * LEDGER_PAGE, ledgerPage * LEDGER_PAGE + LEDGER_PAGE);

  // Interest is what the balance costs you, not what you hand over. This used to
  // sum `monthly`, the payment, and label it interest: a 0% APR debt with a
  // ₱7,725 payment counted as ₱7,725 of interest, while a 36% card with no
  // `monthly` set counted as zero and rendered "no interest" beside its own
  // "36% APR" chip. The two numbers are opposites in what they tell you, since
  // paying more is good and owing more interest is not.
  // Order matters: cards first because they are the expensive, open-ended end of
  // the list, and what is owed to a person last because it is the one entry
  // where the relationship outranks the number.
  const DEBT_KINDS: [string, string][] = [
    ["card", "Credit cards"],
    ["bnpl", "Buy now, pay later"],
    ["loan", "Loans"],
    ["personal", "Owed to people"],
  ];
  // A statement beats a model. `apr` x `balance` assumes the headline rate
  // applies to everything owed, which is wrong for any card carrying an
  // instalment plan, since that plan's interest was fixed when it was
  // converted. Across the four cards the computed figure ran ₱3,869.74 a month
  // above what the August 2026 statements actually charged.
  const monthlyInterest = (d: { balance?: number; apr?: number; finance_charge?: number }) => {
    const charged = Number(d.finance_charge);
    if (Number.isFinite(charged) && charged > 0) return charged;
    return ((Number(d.balance) || 0) * (Number(d.apr) || 0)) / 1200;
  };
  const debtInterest = peso(activeDebts.reduce((t, d) => t + monthlyInterest(d), 0));
  const debtBalances = activeDebts.filter((d) => monthlyInterest(d) > 0).length;
  const paidTotal = paymentHistory.reduce((t, p) => t + p.amount, 0);
  const paidPrin = paymentHistory.reduce((t, p) => t + (typeof p.principal === "number" && Number.isFinite(p.principal) ? p.principal : 0), 0);
  const paidInt = paymentHistory.reduce((t, p) => t + (typeof p.interest === "number" && Number.isFinite(p.interest) ? p.interest : 0), 0);
  const unsplitTotal = paymentHistory.filter((p) => !completeSplit(p)).reduce((t, p) => t + p.amount, 0);
  const owing = payableDebts.reduce((t, d) => t + d.balance, 0);
  const started = payableDebts.reduce((t, d) => t + (d.start || d.balance), 0) || 1;
  const clearedPct = Math.round((1 - owing / started) * 100);

  const stat = (label: string, val: string, color: string) => (
    <div>
      <div style={{ fontFamily: "var(--mono)", fontSize: 11, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--mut)" }}>{label}</div>
      <div style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", fontSize: 18, fontWeight: 700, marginTop: 7, whiteSpace: "nowrap", color }}>{val}</div>
    </div>
  );

  const accTypes: [string, string][] = [["bank", "Bank"], ["ewallet", "E-wallet"], ["cash", "Cash"]];
  const ledgerTabs: [string, string][] = [["activity", "All activity"], ["in", "Income"], ["out", "Spending"]];
  const BANK = ACC_META.bank ?? { fa: "fa-solid fa-building-columns", color: "var(--blue)", tint: "rgba(73,184,234,.14)" };

  // The one thing this page should answer without being read: does what I hold
  // reach the next payday. Everything else on the overview was true and none of
  // it said that, so the page had to be interpreted rather than glanced at.
  // Use current account state for both cash displays, including optimistic
  // ledger updates. Keep the API's independent net-position figure intact.
  const o = st.outlook && { ...st.outlook, onHand: cashOnHand, after: cashAfterDebits(cashOnHand, st.outlook.debitTotal) };
  const glance = !o || !o.nextIn ? null : (
    <div style={{ ...cardBox, borderColor: o.after < 0 ? "var(--danger-line)" : "var(--line)" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
        <span style={{ fontFamily: "var(--mono)", fontSize: 11, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--mut)" }}>Until {o.nextIn.date}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--mut)" }}>{o.dueBefore.length} due · then {peso(o.nextIn.amount)} in</span>
      </div>

      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
        <span style={{ fontFamily: "var(--display)", fontVariantNumeric: "tabular-nums", fontSize: "clamp(30px,4.6vw,42px)", fontWeight: 700, letterSpacing: "-0.01em", lineHeight: 1, color: o.after < 0 ? "var(--danger-ink)" : "var(--green)" }}>
          {(o.after < 0 ? "−" : "") + peso(Math.abs(o.after))}
        </span>
        <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--sub)" }}>{o.after < 0 ? "cash short before payday" : "cash left after debit obligations"}</span>
      </div>

      {/* The arithmetic in full, because a single figure is only trustworthy if
          the two numbers behind it are visible. */}
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginTop: 14, fontSize: 12.5, fontWeight: 700 }}>
        <span style={{ color: "var(--mut)" }}>on hand <b style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", color: "var(--ink)" }}>{peso(o.onHand)}</b></span>
        <span style={{ color: "var(--mut)" }}>debit due <b style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", color: "var(--orange-ink)" }}>{peso(o.debitTotal)}</b></span>
        <span style={{ color: "var(--mut)" }}>credit charges <b style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", color: "var(--accent-ink)" }}>{peso(o.creditTotal)}</b></span>
        <span style={{ color: "var(--mut)" }}>total obligations <b style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", color: "var(--ink)" }}>{peso(o.dueTotal)}</b></span>
        <span style={{ color: "var(--mut)" }}>net position <b style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", color: o.net < 0 ? "var(--danger-ink)" : "var(--ink)" }}>{(o.net < 0 ? "−" : "") + peso(Math.abs(o.net))}</b></span>
      </div>
      {o.creditTotal > 0 && (
        <div style={{ marginTop: 9, fontSize: 11.5, lineHeight: 1.5, color: "var(--mut)" }}>Credit charges add to a balance; they are not deducted from the cash-shortfall figure.</div>
      )}

      {o.dueBefore.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", marginTop: 14 }}>
          {o.dueBefore.map((x, i) => (
            <div key={x.iso + x.source + i} data-meta-row style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0", borderTop: "1px solid var(--line)", fontSize: 12.5 }}>
              <span style={{ fontWeight: 700, color: "var(--mut)", minWidth: 52, flex: "0 0 auto" }}>{x.date}</span>
              <span style={{ fontWeight: 700, flex: "1 1 120px", minWidth: 0 }}>{x.source}</span>
              <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 7, flexWrap: "wrap", flex: "0 0 auto" }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", color: x.kind === "debt" ? "var(--orange)" : "var(--mut)" }}>{x.kind}</span>
                <span
                  title={x.funding === "credit" ? "Charged to credit; not deducted from cash now" : "Leaves cash before payday"}
                  aria-label={`Funding: ${x.funding}. ${x.funding === "credit" ? "Charged to credit; not deducted from cash now" : "Leaves cash before payday"}`}
                  style={{ fontFamily: "var(--mono)", fontSize: 9.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: x.funding === "credit" ? "var(--accent-ink)" : "var(--sub)", background: x.funding === "credit" ? "color-mix(in srgb, var(--accent) 10%, transparent)" : "var(--card2)", border: `1px solid ${x.funding === "credit" ? "var(--ok-line)" : "var(--line2)"}`, borderRadius: 999, padding: "3px 7px" }}
                >
                  {x.funding}
                </span>
              </span>
              <span style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", fontWeight: 700, minWidth: 88, textAlign: "right", flex: "0 0 auto" }}>{peso(x.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const overview = (
    <div style={cardBox}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontFamily: "var(--mono)", fontSize: 11, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--mut)" }}>Money on hand</div>
          <div style={{ fontFamily: "var(--display)", fontVariantNumeric: "tabular-nums", fontSize: "clamp(30px,4.6vw,42px)", fontWeight: 700, letterSpacing: "-0.01em", lineHeight: 1, marginTop: 10 }}>{onHand}</div>
        </div>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
          {stat("In this month", peso(monthIn), "var(--green)")}
          {stat("Spent", peso(monthSpend), "var(--orange-ink)")}
          {stat("To debt", peso(monthDebt), "var(--orange)")}
          {stat("Net", (net >= 0 ? "+" : "−") + peso(Math.abs(net)), net >= 0 ? "var(--green)" : "var(--orange)")}
        </div>
      </div>

      {/* Coming in. Deliberately below the on-hand figure and visually quieter:
          this is money that has not arrived, and it must never be mistaken for
          the balance. But without it the board could only ever show outflows,
          so a month containing a salary read as a shortfall. */}
      {[
        ["Coming in", st.upcomingIncome, "var(--green-ink)", "var(--green)"] as const,
        // Standing costs sit beside the paydays rather than in their own card:
        // the useful question is what is due before the next money arrives, and
        // that only reads at a glance if the two are on the same line.
        ["Going out", st.upcomingExpense, "var(--orange-ink)", "var(--orange)"] as const,
      ].map(([label, rows, ink, tint]) => rows.length === 0 ? null : (
        <div key={label} style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 14, paddingTop: 13, borderTop: "1px solid var(--line)" }}>
          <span style={{ fontFamily: "var(--mono)", fontSize: 10.5, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--mut)", minWidth: 74 }}>{label}</span>
          {rows.slice(0, 4).map((u) => (
            <span key={u.iso + u.source} title={u.source} style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 11.5, fontWeight: 700, color: ink, background: `color-mix(in srgb, ${tint} 10%, transparent)`, border: "1px solid var(--line)", padding: "5px 11px", borderRadius: 999 }}>
              <span style={{ color: "var(--mut)" }}>{u.date}</span>
              <b style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums" }}>{peso(u.amount)}</b>
            </span>
          ))}
          {rows.length > 4 && <span style={{ fontSize: 11, fontWeight: 700, color: "var(--mut)" }}>+{rows.length - 4} more</span>}
        </div>
      ))}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,168px),1fr))", gap: 11, marginTop: 20 }}>
        {st.accounts.map((a) => {
          const m = ACC_META[a.type] || BANK;
          return (
            <button key={a.id} type="button" onClick={() => st.openAccount2(a.id)} style={{ display: "flex", flexDirection: "column", gap: 11, padding: "14px 15px", borderRadius: 10, background: "var(--card2)", border: "1px solid var(--line)", cursor: "pointer", textAlign: "left", font: "inherit", color: "inherit" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 30, height: 30, flex: "0 0 auto", borderRadius: 8, display: "grid", placeItems: "center", background: m.tint, color: m.color }}>
                  <i className={m.fa} style={{ fontSize: 14, lineHeight: 1 }} />
                </span>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--ink)", minWidth: 0 }}>{a.name}</span>
              </span>
              <span style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", fontSize: 18, fontWeight: 700, lineHeight: 1 }}>{peso(a.balance)}</span>
              <span style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontFamily: "var(--mono)", fontSize: 10, fontWeight: 600, letterSpacing: ".1em", textTransform: "uppercase", color: m.color }}>{a.type === "ewallet" ? "e-wallet" : a.type}</span>
                {/* Only when a rate is actually recorded. Rendering "0%" on every
                    untracked account would claim they pay nothing, which is a
                    different statement from not knowing. Monthly earnings shown
                    beside it, since that is the number worth comparing against a
                    debt's monthly interest. */}
                {!!a.apr && (
                  <span title={peso((a.balance * a.apr) / 1200) + " / mo"} style={{ fontFamily: "var(--mono)", fontSize: 10, fontWeight: 700, letterSpacing: ".04em", color: "var(--green-ink)" }}>{a.apr}% p.a.</span>
                )}
              </span>
            </button>
          );
        })}
        <Button variant="quiet" icon="fa-solid fa-plus" onClick={st.openAccount} className="finance-add-account">Add account</Button>
      </div>

      {st.addingAccount && (
        <div className="finance-form" role="group" aria-label="Add an account">
          <Field label="Account name" value={st.accName} onChange={(event) => st.setAccName(event.target.value)} placeholder="For example, Seabank" />
          <fieldset className="finance-form-group">
            <legend>Account type</legend>
            <div className="finance-form-picks">
              {accTypes.map(([key, label]) => <Fchip key={key} label={label} on={st.accType === key} onSelect={() => st.setAccType(key)} />)}
            </div>
          </fieldset>
          <Field label="Starting balance (₱)" inputMode="decimal" value={st.accBalance} onChange={(event) => st.setAccBalance(event.target.value)} placeholder="0.00" />
          {st.financeError && <p className="ds-field-error" role="alert">{st.financeError}</p>}
          <div className="finance-form-actions">
            <Button onClick={st.cancelAccount}>Cancel</Button>
            <Button variant="primary" onClick={st.saveAccount}>Add account</Button>
          </div>
        </div>
      )}
    </div>
  );

  // Everything that moves money: the log buttons, the category split, the forms
  // and the ledger. Split from the balances above because "what do I have" and
  // "what happened" are different questions, asked at different moments.
  // Annualised, because a quarterly premium and a monthly subscription are not
  // comparable until both are. Only the forms the projector understands count.
  const perYear = (recurs: string): number => {
    const m = /^monthly:\d{1,2}$/i.test(recurs) ? 12 : 0;
    if (m) return m;
    const e = /^every:(\d{1,2})([dwmy]):/i.exec(recurs);
    if (!e) return 0;
    const n = Number(e[1]) || 1;
    const u = String(e[2]).toLowerCase();
    return u === "d" ? 365 / n : u === "w" ? 52 / n : u === "m" ? 12 / n : 1 / n;
  };
  const standingYear = st.standing.reduce((t, s) => t + s.amount * perYear(s.recurs), 0);
  // Grouped by cadence, largest first within each group. Twelve arrangements in
  // one flat list was the pollution: a monthly subscription and a quarterly
  // premium are different kinds of commitment, and interleaved they can only be
  // read, never scanned. Frequent cadences first, unparseable recurrence last.
  const byCadence = new Map<number, typeof st.standing>();
  for (const s of st.standing) {
    const k = perYear(s.recurs);
    const g = byCadence.get(k);
    if (g) g.push(s); else byCadence.set(k, [s]);
  }
  const standingGroups = [...byCadence.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([n, rows]) => ({ n, rows: rows.slice().sort((a, b) => b.amount - a.amount) }));
  // Without this the amount is ambiguous: 7,628.50 quarterly and 7,038.02
  // monthly sat in the same column looking like the same kind of number, and
  // only the annual figure beside them gave the game away.
  const cadence = (recurs: string): string => {
    const n = perYear(recurs);
    if (n === 12) return "per month";
    if (n === 4) return "per quarter";
    if (n === 1) return "per year";
    if (n === 52) return "per week";
    if (n === 2) return "twice a year";
    if (n === 26) return "fortnightly";
    return n > 0 ? `${Math.round(n)}x a year` : "";
  };

  const flow = (
    <div style={cardBox}>
      <div className="finance-actions">
        <Button variant="primary" icon="fa-solid fa-arrow-down" onClick={st.openIncome}>Log income</Button>
        <Button icon="fa-solid fa-arrow-up" onClick={st.openExpense}>Log expense</Button>
        <Button icon="fa-solid fa-right-left" onClick={st.openMove}>Transfer</Button>
      </div>

      <div role="group" aria-label="Ledger date range" className="finance-date-range">
        <div className="finance-date-controls">
          <div className="finance-date-fields">
            <Field label="Start date" type="date" value={dateStart} max={dateEnd || undefined} aria-invalid={dateRangeInvalid || undefined} aria-describedby={`${ledgerId}-date-range-help`} onChange={(e) => { setDateStart(e.target.value); setLedgerP(0); }} />
            <Field label="End date" type="date" value={dateEnd} min={dateStart || undefined} aria-invalid={dateRangeInvalid || undefined} aria-describedby={`${ledgerId}-date-range-help`} onChange={(e) => { setDateEnd(e.target.value); setLedgerP(0); }} />
            {dateRangeActive && <Button onClick={() => { setDateStart(""); setDateEnd(""); setLedgerP(0); }}>Clear dates</Button>}
          </div>
          <div id={`${ledgerId}-date-range-help`} role={dateRangeInvalid ? "alert" : undefined} className={`finance-date-help${dateRangeInvalid ? " finance-date-help--error" : ""}`}>
            {dateRangeInvalid ? "End date must be on or after start date." : dateRangeActive ? "Both dates are included. Use the same date for one day." : "All dates. Choose a start and end date, or the same date for one day."}
          </div>
        </div>
        <div role="status" aria-live="polite" aria-atomic="true" className="finance-date-total">
          {stat(selectedCategory ? `${selectedCategory.label} expenses` : "Total expenses", dateRangeInvalid ? "Unavailable" : peso(selectedSpend), "var(--orange-ink)")}
          {!dateRangeInvalid && <div className="finance-date-count">{selectedExpenses.length} {selectedExpenses.length === 1 ? "expense" : "expenses"} · {dateRangeActive ? "Selected dates" : "All dates"}</div>}
        </div>
      </div>

      <div className="finance-category-filters">
        {spendCats.map(({ c, sum }) => {
          const on = catFilter === c.key;
          return (
            <Button
              key={c.key}
              size="compact"
              variant={on ? "primary" : "secondary"}
              onClick={() => { setCatFilter(on ? null : c.key); setLedgerP(0); }}
              aria-pressed={on}
              title={on ? `Clear ${c.label} filter` : `Filter ledger to ${c.label}`}
              className="finance-category-filter"
              icon={<i className={c.fa} style={{ color: on ? "var(--on-accent)" : c.color }} />}
            >
              <span>{c.label}</span>
              <span className="finance-figure">{peso(sum)}</span>
              <span style={{ color: on ? "var(--on-accent)" : c.color }}>{Math.round((sum / (totalSpend || 1)) * 100)}%</span>
            </Button>
          );
        })}
      </div>

      {st.addingIncome && (
        <div className="finance-form" role="group" aria-label="Log income">
          <div className="finance-form-fields">
            <Field label="Income amount (₱)" inputMode="decimal" value={st.incAmount} onChange={(event) => st.setIncAmount(event.target.value)} placeholder="0.00" />
            <Field label="Income source" value={st.incSource} onChange={(event) => st.setIncSource(event.target.value)} placeholder="For example, salary" />
          </div>
          <fieldset className="finance-form-group">
            <legend>Lands in</legend>
            <div className="finance-form-picks">
              {st.accounts.map((a) => <Fchip key={a.id} label={a.name} on={st.incAccount === a.id} onSelect={() => st.setIncAccount(a.id)} />)}
            </div>
          </fieldset>
          {st.financeError && <p className="ds-field-error" role="alert">{st.financeError}</p>}
          <div className="finance-form-actions">
            <Button onClick={st.cancelIncome}>Cancel</Button>
            <Button variant="primary" onClick={st.saveIncome}>Add income</Button>
          </div>
        </div>
      )}

      {st.addingExpense && (
        <div className="finance-form" role="group" aria-label="Log an expense">
          <div className="finance-form-fields">
            <Field label="Expense amount (₱)" inputMode="decimal" value={st.expAmount} onChange={(event) => st.setExpAmount(event.target.value)} placeholder="0.00" />
            <Field label="What was the expense?" value={st.expNote} onChange={(event) => st.setExpNote(event.target.value)} placeholder="For example, groceries" />
          </div>
          <fieldset className="finance-form-group">
            <legend>Category</legend>
            <div className="finance-form-picks">
              {EXP_CATS.map((c) => <Fchip key={c.key} label={c.label} on={st.expCat === c.key} icon={c.fa} onSelect={() => st.setExpCat(c.key)} />)}
            </div>
          </fieldset>
          <fieldset className="finance-form-group">
            <legend>Paid from</legend>
            <div className="finance-form-picks">
              {st.accounts.map((a) => <Fchip key={a.id} label={a.name} on={st.expAccount === a.id} onSelect={() => st.setExpAccount(a.id)} />)}
            </div>
          </fieldset>
          {st.financeError && <p className="ds-field-error" role="alert">{st.financeError}</p>}
          <div className="finance-form-actions">
            <Button onClick={st.cancelExpense}>Cancel</Button>
            <Button variant="primary" onClick={st.saveExpense}>Add expense</Button>
          </div>
        </div>
      )}

      {st.movingMoney && (
        <div className="finance-form" role="group" aria-label="Transfer money">
          <Field label="Amount to move (₱)" inputMode="decimal" value={st.mvAmount} onChange={(event) => st.setMvAmount(event.target.value)} placeholder="0.00" />
          <fieldset className="finance-form-group">
            <legend>From</legend>
            <div className="finance-form-picks">
              {st.accounts.map((a) => (
                <Button key={a.id} size="compact" variant={st.mvFrom === a.id ? "primary" : "secondary"} aria-pressed={st.mvFrom === a.id} onClick={() => st.setMvFrom(a.id)} className="finance-account-pick">
                  <span>{a.name}</span><span className="finance-figure">{peso(a.balance)}</span>
                </Button>
              ))}
            </div>
          </fieldset>
          <fieldset className="finance-form-group">
            <legend>To</legend>
            <div className="finance-form-picks">
              {st.accounts.map((a) => (
                <Button key={a.id} size="compact" variant={st.mvTo === a.id ? "primary" : "secondary"} aria-pressed={st.mvTo === a.id} onClick={() => st.setMvTo(a.id)} className="finance-account-pick">
                  <span>{a.name}</span><span className="finance-figure">{peso(a.balance)}</span>
                </Button>
              ))}
            </div>
          </fieldset>
          {st.financeError && <p className="ds-field-error" role="alert">{st.financeError}</p>}
          <div className="finance-form-actions">
            <Button onClick={st.cancelMove}>Cancel</Button>
            <Button variant="primary" onClick={st.saveMove}>Move it</Button>
          </div>
        </div>
      )}

      {/* Standing costs: the arrangements, not their occurrences. They sit
          above the ledger because "what do I pay on repeat" is a money in and
          out question the ledger cannot answer - a recurring row is
          deliberately excluded from spend. Collapsed to the roll-up by
          default, and grouped by cadence when open, because amounts on
          different cadences are not comparable until sorted into kind. */}
      {st.standing.length > 0 && (
        <div style={{ marginTop: 22 }}>
          <Button variant="quiet" size="compact" onClick={() => setStandingOpen((value) => !value)} aria-expanded={standingOpen} className="finance-standing-toggle">
            <span style={{ fontFamily: "var(--mono)", fontSize: 11, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--mut)" }}>Standing costs<span style={{ marginLeft: 8, color: "var(--faint)" }}>{st.standing.length}</span></span>
            <span style={{ marginLeft: "auto", fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", fontSize: 11.5, fontWeight: 700, color: "var(--orange-ink)" }}>≈ {peso(standingYear / 12)} / mo · {peso(standingYear)} / yr</span>
            <i className={`fa-solid fa-chevron-${standingOpen ? "up" : "down"}`} aria-hidden="true" style={{ fontSize: 14, color: "var(--mut)", alignSelf: "center" }} />
          </Button>
          {standingOpen && standingGroups.map((g) => (
            <div key={g.n}>
              {/* Cadence lives on the group, not the row: with the list sorted
                  into kinds, "per month" printed twelve times over was noise.
                  The subtotal is per occurrence, which is only meaningful
                  because everything under it recurs at the same rate. */}
              <div style={{ display: "flex", alignItems: "baseline", gap: 12, padding: "12px 2px 5px" }}>
                <span style={{ fontFamily: "var(--mono)", fontSize: 10.5, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--faint)" }}>{g.n === 0 ? "No cadence set" : cadence(g.rows[0]!.recurs)}<span style={{ marginLeft: 8 }}>{g.rows.length}</span></span>
                <span style={{ marginLeft: "auto", fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", fontSize: 11, fontWeight: 700, color: "var(--mut)" }}>{peso(g.rows.reduce((t, s) => t + s.amount, 0))}</span>
              </div>
              {g.rows.map((s) => {
                const c = EXP_CATS.find((x) => x.key === (s.cat || "").toLowerCase());
                const yr = s.amount * perYear(s.recurs);
                return (
                  <button key={s.id} type="button" onClick={() => st.openExpense2(s.id)} style={{ display: "flex", alignItems: "center", gap: 13, flexWrap: "wrap", padding: "11px 2px", width: "100%", textAlign: "left", color: "var(--ink)", background: "transparent", border: "none", borderTop: "1px solid var(--line)", cursor: "pointer" }}>
                    {/* The cost's own mark when it has one. A brand icon is the
                        fastest thing to find in a list you scan rather than
                        read, and three different subscriptions all showing the
                        "other" ellipsis made this column pure decoration. */}
                    <span style={{ width: 28, height: 28, flex: "0 0 auto", borderRadius: 8, display: "grid", placeItems: "center", background: "var(--card2)", color: s.color || c?.color || "var(--mut)" }}>
                      <i className={s.icon || c?.fa || "fa-solid fa-rotate"} style={{ fontSize: 12, lineHeight: 1 }} />
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, flex: "1 1 130px", minWidth: 0 }}>{s.note}</span>
                    {yr > 0 && <span style={{ fontSize: 11.5, fontWeight: 700, color: "var(--mut)", flex: "0 0 auto" }}>{peso(yr)} / yr</span>}
                    <span style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", fontSize: 13, fontWeight: 700, minWidth: 88, textAlign: "right", flex: "0 0 auto", color: "var(--orange-ink)" }}>{peso(s.amount)}</span>
                    <i className="fa-solid fa-chevron-right" style={{ fontSize: 12, color: "var(--mut)", flex: "0 0 auto" }} />
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginTop: 22 }}>
        <div style={{ fontFamily: "var(--mono)", fontSize: 11, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--mut)" }}>Ledger</div>
        <Tabs items={ledgerTabs.map(([id, label]) => ({ id, label }))} value={catFilter ? "" : st.ledger} onChange={(id) => { st.setLedger(id); setCatFilter(null); setLedgerP(0); }} label="Ledger activity" panelId={ledgerId} className="finance-ledger-tabs" />
      </div>

      <div id={ledgerId} role="tabpanel" aria-label={catFilter ? "Spending in the selected category" : ledgerTabs.find(([id]) => id === st.ledger)?.[1] || "All activity"} style={{ display: "flex", flexDirection: "column", marginTop: 8 }}>
        {ledgerRows.map((lr, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 13, flexWrap: "wrap", padding: "13px 0", borderTop: "1px solid var(--line)" }}>
            <span style={{ width: 28, height: 28, flex: "0 0 auto", borderRadius: 8, display: "grid", placeItems: "center", background: "var(--card2)", color: lr.dot }}>
              <i className={lr.fa} style={{ fontSize: 11, lineHeight: 1 }} />
            </span>
            <span style={{ fontSize: 11.5, fontWeight: 700, color: "var(--mut)", minWidth: 48, flex: "0 0 auto" }}>{lr.date}</span>
            <span style={{ fontSize: 13, fontWeight: 700, flex: "1 1 130px", minWidth: 0 }}>{lr.label}</span>
            <span style={{ fontSize: 11.5, fontWeight: 700, color: "var(--mut)", flex: "0 0 auto" }}>{lr.account}</span>
            <span style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", fontSize: 13, fontWeight: 700, minWidth: 88, textAlign: "right", flex: "0 0 auto", color: lr.color }}>{lr.amount}</span>
          </div>
        ))}
        {sortedLedger.length > LEDGER_PAGE && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: "13px 0 22px", borderTop: "1px solid var(--line)" }}>
            <span style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", fontSize: 11, fontWeight: 700, color: "var(--mut)" }}>
              Page {ledgerPage + 1} of {ledgerPages} · {sortedLedger.length} entries
            </span>
            <div className="finance-pager">
              <Button size="compact" icon="fa-solid fa-chevron-left" disabled={ledgerPage === 0} onClick={() => setLedgerP(ledgerPage - 1)}>Prev</Button>
              <Button size="compact" icon="fa-solid fa-chevron-right" disabled={ledgerPage >= ledgerPages - 1} onClick={() => setLedgerP(ledgerPage + 1)}>Next</Button>
            </div>
          </div>
        )}
        {/* An empty ledger looked like a broken page rather than an empty one,
            and the filter above it made it worse: "Spending" with nothing under
            it reads as data failing to load. Say which of the two it is. */}
        {ledgerRows.length === 0 && !dateRangeInvalid && (
          <div style={{ padding: "26px 0 18px", textAlign: "center", borderTop: "1px solid var(--line)" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--sub)" }}>
              {dateRangeActive || catFilter ? "No entries match these filters." : st.ledger === "out" ? "No spending logged yet." : st.ledger === "in" ? "No income logged yet." : "Nothing logged yet."}
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--mut)", marginTop: 6, textWrap: "pretty" }}>
              {dateRangeActive || catFilter ? "Try different dates or clear the filters to see more activity." : "Use Log income or Log expense above to add your first entry."}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // The payoff plan, rendered inside the HUD rather than linked out. Private by
  // construction: it sits behind the same sign-in as everything else, instead of
  // depending on wherever a separate page happened to be hosted.
  //
  // Ordered by `rank`, set per debt to follow what a peso actually saves rather
  // than the headline APR. An add-on loan normally rewards early payment with
  // nothing, because its interest was fixed at origination, so a 47% one would
  // sit below a 36% card. Shopee rebating the unearned interest is what flips
  // that, and no rate on its own can express it.
  const ranked = activeDebts.filter((d) => (d.rank ?? 0) > 0).sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0));
  // What a month costs before food, fuel or anything lived on. Deliberately not
  // netted against pay: the recurrence rules behind a salary never reach the
  // HUD, and inventing an income figure to make the total agree would be worse
  // than showing the floor on its own.
  const floorDebt = activeDebts.reduce((t, d) => t + (d.min_payment || d.monthly || d.plan_monthly || 0), 0);
  const floorStanding = standingYear / 12;

  // Every peso landing in the projection window, dated, with the cash balance
  // run through it. A monthly total cannot answer the question this does: the
  // month can close positive while the account empties for two days in the
  // middle of it, and it is the empty two days that bounce a debit.
  //
  // The window is the 60 days the API already projects income and standing
  // costs over. Everything here is derived, so a logged payment or a moved
  // balance reshapes it on the next load.
  const HORIZON_DAYS = 60;
  const horizonIso = (() => {
    const d = new Date(Date.now() + 8 * 3600 * 1000); // Manila, per DESIGN.md
    d.setUTCDate(d.getUTCDate() + HORIZON_DAYS);
    return d.toISOString().slice(0, 10);
  })();
  const todayIso = new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 10);

  const { startCash, ledger } = buildCashForecast(st.accounts, st.upcomingIncome, st.upcomingExpense, st.upcomingDebt, horizonIso);
  // The number the whole table exists to surface.
  const trough = ledger.reduce((lo, r) => (r.after < lo.after ? r : lo), { after: startCash, iso: todayIso, label: "", amount: 0, kind: "in" as const });
  const dayOf = (iso: string) => {
    const [, mm, dd] = iso.split("-").map(Number);
    return mm && dd ? `${CUR_MONTHS[mm - 1]} ${dd}` : iso;
  };

  // Which lines actually cost something. A headline rate does not answer this:
  // Atome quotes the highest rate on the board and costs nothing on anything
  // cleared within the month, while a card quoting less charges every peso left
  // on it. Sorting the lines by what they charge in practice is the difference
  // between a plan and a list of scary percentages.
  const byKind = (k: string) => activeDebts.filter((d) => (d.kind ?? "loan") === k);
  const cardsOwed = byKind("card").reduce((t, d) => t + d.balance, 0);
  const cardsInt = byKind("card").reduce((t, d) => t + monthlyInterest(d), 0);
  const atomeDebts = activeDebts.filter((d) => (d.facility ?? "") === "atome");
  const atomeOwed = atomeDebts.reduce((t, d) => t + d.balance, 0);
  const atomeInt = atomeDebts.reduce((t, d) => t + monthlyInterest(d), 0);
  const spay = activeDebts.find((d) => d.id === "spaylater");

  const LINES: [string, string, string, string, boolean][] = [
    ["Credit cards", peso(cardsOwed), peso(cardsInt) + " / mo", "Revolving at 3% a month on whatever is left. This is the one that actually charges you.", true],
    ["Atome instalment plans", peso(atomeOwed), peso(atomeInt) + " / mo", "Add-on at 4.5%, fixed when each plan was written. Nothing to save by rushing them; they clear on their own.", true],
    ["Atome monthly charges", "cleared in full", "nothing", "Claude, the gym, Spotify, Grab. Paid off every month by rule, so the 4.5% never attaches. A payment rail, not a lender.", false],
    ["SPayLater", spay ? peso(spay.balance) : "0.00", "nothing", "Utilities split over 3 months at 0%. A deferral, not financing, and the right tool for a month that does not close.", false],
  ];

  const PHASES: [string, string, string, string, string][] = [
    ["Survive", "Now", "Minimums only", "Pay every minimum, never late, and keep enough back to clear the Atome balance each month. Prepay nothing, however good the return looks.", "var(--danger)"],
    ["Settle", "When there is surplus", "Take the rebates", "Clear the loans whose lender hands back unearned interest on early settlement. That frees their monthly payment as well as the interest.", "var(--orange)"],
    ["Attack", "Once the instalments end", "Smallest card first", "Every spare peso at the cards. They are the only balances that keep charging on whatever is left, so each peso saves there forever.", "var(--accent)"],
  ];

  // Only interest-bearing behaviour belongs here. An earlier version banned
  // buy-now-pay-later outright and told him to keep bills off every credit
  // line, which forbade the two things on this board that cost nothing.
  const RULES: string[] = [
    "No new Atome instalment plans",
    "No new card spend",
    "No new loan to cover a gap",
    "Never carry a card balance by choice",
  ];

  const planRow = (d: typeof st.debts[number], i: number) => {
    const rate = (d.apr ?? 0) / 12;
    return (
      <div key={d.id} className="finance-plan-row" style={{ borderTop: i === 0 ? "none" : "1px solid var(--line)" }}>
        <span style={{ fontFamily: "var(--mono)", fontSize: 12, fontWeight: 700, textAlign: "center", color: i < 3 ? "var(--on-accent)" : "var(--mut)", background: i < 3 ? "var(--accent-fill)" : "transparent", borderRadius: 999, padding: "3px 0" }}>{d.rank}</span>
        <span className="finance-plan-title" style={{ fontSize: 14, fontWeight: 700 }}>{d.name}</span>
        <span className="finance-plan-values">
          <span style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", fontSize: 12, fontWeight: 700, color: rate > 0 ? "var(--orange-ink)" : "var(--mut)" }}>{rate > 0 ? rate.toFixed(2) + "% / mo" : "no interest"}</span>
          <span style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", fontSize: 13, fontWeight: 700 }}>{peso(d.balance)}</span>
        </span>
      </div>
    );
  };

  const plan = (
    <>
      <div style={cardBox}>
        <div style={{ fontFamily: "var(--mono)", fontSize: 11, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--mut)" }}>What a month costs, at the floor</div>
        <div style={{ display: "flex", gap: 30, flexWrap: "wrap", marginTop: 14 }}>
          {stat("Debt minimums", peso(floorDebt), "var(--orange)")}
          {stat("Standing costs", peso(floorStanding), "var(--sub)")}
          {stat("Together", peso(floorDebt + floorStanding), "var(--danger-ink)")}
        </div>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--mut)", marginTop: 14 }}>
          Before food, fuel or groceries, none of which are in the ledger yet. Treat this as a floor, not an estimate.
        </div>
      </div>

      {ledger.length > 0 && (
        <div style={cardBox}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
            <div style={{ fontFamily: "var(--mono)", fontSize: 11, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--mut)" }}>The next {HORIZON_DAYS} days, every peso</div>
            <div style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", fontSize: 12, fontWeight: 700, color: "var(--mut)" }}>starting {peso(startCash)}</div>
          </div>

          {/* The lowest the balance gets, called out because it is the whole
              point of a dated ledger and it is invisible in a monthly total. */}
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginTop: 14, padding: "13px 15px", borderRadius: 8, background: "var(--card2)", border: `1px solid ${trough.after < 0 ? "var(--danger-line)" : "var(--line)"}` }}>
            <span style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", fontSize: 21, fontWeight: 700, color: trough.after < 0 ? "var(--danger-ink)" : "var(--accent-ink)" }}>{peso(trough.after)}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--sub)" }}>
              {trough.after < 0 ? "short on " : "lowest point, "}{dayOf(trough.iso)}
            </span>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--mut)" }}>
              {trough.after < 0 ? "after " + trough.label + ". Money has to be moved before this date." : "after " + trough.label + "."}
            </span>
          </div>

          <div style={{ maxHeight: 430, overflowY: "auto", marginTop: 12 }}>
            {ledger.map((r, i) => (
              <div key={r.iso + r.label + i} className="finance-forecast-row" style={{ borderTop: i === 0 ? "none" : "1px solid var(--line)" }}>
                <span style={{ fontFamily: "var(--mono)", fontSize: 11.5, fontWeight: 700, color: "var(--mut)" }}>{dayOf(r.iso)}</span>
                <span className="finance-forecast-label" style={{ fontSize: 13.5, fontWeight: 600 }}>{r.label}</span>
                <span className="finance-forecast-values">
                  <span style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", fontSize: 12.5, fontWeight: 700, textAlign: "right", whiteSpace: "nowrap", color: r.amount > 0 ? "var(--green-ink)" : "var(--sub)" }}>
                    {(r.amount > 0 ? "+" : "") + peso(r.amount)}
                  </span>
                  <span className="finance-forecast-balance" style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", fontSize: 12.5, fontWeight: 700, color: r.after < 0 ? "var(--danger-ink)" : r.after < 10000 ? "var(--orange-ink)" : "var(--mut)" }}>
                    <span className="finance-forecast-balance-label">After </span>{peso(r.after)}
                  </span>
                </span>
              </div>
            ))}
          </div>

          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--mut)", marginTop: 12 }}>
            Every account counted as one pool. Each debit actually hits one account, so a total that looks fine can still bounce. Food, fuel and groceries are not in here at all.
          </div>
        </div>
      )}

      {ranked.length > 0 && (
        <div style={cardBox}>
          <div style={{ fontFamily: "var(--mono)", fontSize: 11, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--mut)" }}>Where every spare peso goes</div>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--mut)", marginTop: 8, marginBottom: 6 }}>
            Not sorted by headline rate. Sorted by what a peso actually saves, which depends on how each debt charges.
          </div>
          {ranked.map(planRow)}
        </div>
      )}

      <div style={cardBox}>
        <div style={{ fontFamily: "var(--mono)", fontSize: 11, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--mut)" }}>Which lines actually cost you</div>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--mut)", marginTop: 8 }}>
          A headline rate does not answer this. Atome quotes the highest rate here and charges nothing on what is cleared inside the month.
        </div>
        {LINES.map(([name, owed, cost, why, charging], i) => (
          <div key={name} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "4px 16px", padding: "13px 0", borderTop: i === 0 ? "none" : "1px solid var(--line)" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 9, fontSize: 14.5, fontWeight: 700 }}>
              <span style={{ width: 7, height: 7, borderRadius: 999, background: charging ? "var(--danger)" : "var(--accent)" }} />
              {name}
            </span>
            <span style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", fontSize: 12.5, fontWeight: 700, textAlign: "right", whiteSpace: "nowrap", color: charging ? "var(--orange-ink)" : "var(--accent-ink)" }}>{cost}</span>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--mut)", gridColumn: "1 / -1" }}>
              <span style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", color: "var(--sub)" }}>{owed}</span>{"  ·  " + why}
            </span>
          </div>
        ))}
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 14, padding: "13px 15px", borderRadius: 8, background: "var(--card2)", border: "1px solid var(--warn-line)" }}>
          <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: 12, lineHeight: 1.4, color: "var(--warn-ink)" }} />
          <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--sub)" }}>
            The exposure is the rule, not the rate. Clearing Atome monthly works while there is cash to clear it with. A month that cannot converts the whole balance to 4.5% a month, at the worst possible moment. Protect the buffer first.
          </span>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,240px),1fr))", gap: 14 }}>
        {PHASES.map(([name, when, headline, body, color]) => (
          <div key={name} style={{ ...cardBox, display: "flex", flexDirection: "column", gap: 10 }}>
            <span style={{ alignSelf: "flex-start", fontFamily: "var(--mono)", fontSize: 10.5, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color, background: "var(--card2)", border: "1px solid var(--line)", borderRadius: 999, padding: "5px 11px" }}>{when}</span>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{name}</div>
            <div style={{ fontFamily: "var(--mono)", fontSize: 13, fontWeight: 700, color: "var(--sub)" }}>{headline}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--mut)" }}>{body}</div>
          </div>
        ))}
      </div>

      <div style={cardBox}>
        <div style={{ fontFamily: "var(--mono)", fontSize: 11, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--mut)" }}>Rules while the plan runs</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 9, marginTop: 14 }}>
          {RULES.map((r) => (
            <span key={r} style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12.5, fontWeight: 700, color: "var(--danger-ink)", background: "var(--card2)", border: "1px solid var(--danger-line)", borderRadius: 999, padding: "8px 14px" }}>
              <i className="fa-solid fa-ban" style={{ fontSize: 10, lineHeight: 1 }} />{r}
            </span>
          ))}
        </div>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--mut)", marginTop: 14 }}>
          The order above only holds while the balances stop climbing. The full workings live in the vault at Trackers/Finances/Payment strategy.
        </div>
      </div>
    </>
  );

  const debt = (
    <>
      <div style={cardBox}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontFamily: "var(--mono)", fontSize: 11, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--mut)" }}>Debt snowball</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 9, marginTop: 10 }}>
              <span style={{ fontFamily: "var(--display)", fontSize: "clamp(30px,4.6vw,42px)", fontWeight: 700, letterSpacing: "-0.01em", lineHeight: 1, color: "var(--green)" }}>{clearedPct}%</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: "var(--sub)" }}>cleared</span>
            </div>
          </div>
          <div style={{ display: "flex", gap: 26, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontFamily: "var(--mono)", fontSize: 11, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--mut)" }}>Interest / month</div>
              <div style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", fontSize: 20, fontWeight: 700, marginTop: 7, color: "var(--orange)" }}>{debtInterest}</div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--mut)", marginTop: 4 }}>bleeding out every month across {debtBalances} balances</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontFamily: "var(--mono)", fontSize: 11, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--mut)" }}>Still owing</div>
              <div style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", fontSize: 20, fontWeight: 700, marginTop: 7 }}>{peso(owing)}</div>
            </div>
          </div>
        </div>
        <div style={{ height: 8, borderRadius: 4, background: "var(--raise)", overflow: "hidden", marginTop: 16 }}>
          <span style={{ ...fill(clearedPct), borderRadius: 4, background: "var(--green)" }} />
        </div>
        {/* Sits directly under the bar because that is where the question lands:
            the percentage says how far along, and this is the only thing here
            that answers what to do next. */}
        <div style={{ display: "flex", marginTop: 14 }}>
          <Button size="compact" icon="fa-solid fa-route" onClick={() => setTab("plan")}>Read the payoff plan</Button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 0, marginTop: 8 }}>
          {DEBT_KINDS.map(([kind, label]) => {
            const group = activeDebts.filter((d) => (d.kind ?? "loan") === kind);
            if (!group.length) return null;
            const owed = group.reduce((t, d) => t + d.balance, 0);
            const int = group.reduce((t, d) => t + monthlyInterest(d), 0);
            return (
              <div key={kind}>
                {/* A subtotal per group, because the whole point of grouping is
                    comparing the buckets: cards against loans against what is
                    owed to a person. Interest alongside, since two groups can
                    owe similar amounts and cost wildly different money. */}
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: "16px 2px 8px", borderTop: "1px solid var(--line)" }}>
                  <span style={{ fontFamily: "var(--mono)", fontSize: 10.5, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--mut)" }}>{label}<span style={{ marginLeft: 8, color: "var(--faint)" }}>{group.length}</span></span>
                  <span style={{ display: "flex", alignItems: "baseline", gap: 12, fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", fontSize: 11.5, fontWeight: 700 }}>
                    {int > 0 && <span style={{ color: "var(--orange-ink)" }}>{peso(int)} / mo</span>}
                    <span style={{ color: "var(--sub)" }}>{peso(owed)}</span>
                  </span>
                </div>
                {group.map((d) => {
            const pct = Math.round((1 - d.balance / (d.start || d.balance || 1)) * 100);
            const apr = d.apr ?? 0;
            const int = monthlyInterest(d);
            const pay = d.monthly || d.plan_monthly || 0;
            return (
              <button key={d.id} type="button" onClick={() => st.openDebt(d.id)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap", padding: "14px 2px", width: "100%", textAlign: "left", color: "var(--ink)", background: "transparent", border: "none", borderBottom: "1px solid var(--line)", cursor: "pointer" }}>
                <span style={{ minWidth: 0, flex: "1 1 200px" }}>
                  <span style={{ display: "block", fontSize: 14, fontWeight: 700, letterSpacing: "-0.012em", color: "var(--ink)" }}>{d.name}</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
                    {/* Monthly, not APR. Philippine lenders quote a monthly finance
                        charge, so "36% APR" is a number that appears on no statement
                        you hold and has to be divided by twelve in your head before it
                        means anything. The stored field stays annual; only the label
                        converts. Rounded to two places so 3.5% survives. */}
                    <span title={apr ? apr + "% APR nominal" : "interest-free"} style={{ fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 999, background: "var(--card2)", border: "1px solid var(--line)", color: apr >= 30 ? "var(--orange)" : apr > 0 ? "var(--gold)" : "var(--green)" }}>{apr ? +(apr / 12).toFixed(2) + "% / mo" : "interest-free"}</span>
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: int > 0 ? "var(--orange-ink)" : "var(--mut)" }}>{int > 0 ? peso(int) + " / mo interest" : pay > 0 ? peso(pay) + " / mo payment" : "no schedule set"}</span>
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: "var(--mut)" }}>· {d.next}</span>
                    {/* Headroom, not utilisation. "289% of limit" makes you do the
                        subtraction; the money you can still draw is the number you
                        actually act on, and it simply goes negative when the
                        balance is past the ceiling. Minus sign before the peso, as
                        the Net figure above does, since "₱-66,150" reads wrong. */}
                    {!!d.limit && (() => {
                      // `drawn` is the whole shared line when this debt sits on a
                      // facility, so two loans against one limit report the same
                      // headroom rather than each claiming all of it.
                      const drawn = d.drawn ?? d.balance;
                      const avail = d.limit - drawn;
                      const tone = avail < 0 ? "var(--danger-ink)" : avail / d.limit <= 0.2 ? "var(--orange)" : "var(--mut)";
                      const title = d.facility
                        ? `${peso(drawn)} drawn of ${peso(d.limit)} across the ${d.facility} line`
                        : `${peso(drawn)} drawn of ${peso(d.limit)}`;
                      return (
                        <span title={title} style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 999, background: "var(--card2)", border: `1px solid ${avail < 0 ? "var(--danger-line)" : "var(--line)"}`, color: tone }}>
                          {(avail < 0 ? "−" : "") + peso(Math.abs(avail))} left{d.facility ? " · shared" : ""}
                        </span>
                      );
                    })()}
                    {d.public && <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", padding: "3px 8px", borderRadius: 999, color: "var(--accent-ink)", background: "rgba(110,168,255,.14)", border: "1px solid var(--line2)" }}><i className="fa-solid fa-link" style={{ fontSize: 9, marginRight: 5 }} />Shared</span>}
                  </span>
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <span style={{ display: "block", width: "clamp(80px,18vw,150px)", height: 6, borderRadius: 3, background: "var(--raise)", overflow: "hidden" }}>
                    <i style={{ display: "block", height: "100%", borderRadius: 3, background: d.color, width: pct + "%" }} />
                  </span>
                  <span style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", fontSize: 14, fontWeight: 700, minWidth: 92, textAlign: "right" }}>{peso(d.balance)}</span>
                  <i className="fa-solid fa-chevron-right" style={{ fontSize: 12, color: "var(--mut)", flex: "0 0 auto" }} />
                </span>
              </button>
            );
                })}
              </div>
            );
          })}
        </div>
        {receivables.length > 0 && <section className="finance-receivables" aria-label="Owed to you">
          <div className="finance-receivables-heading">
            <span>Owed to you<span className="finance-receivables-count">{activeReceivables.length}</span></span>
            <strong>{paymentPeso(totalReceivable)}</strong>
          </div>
          {activeReceivables.map((d) => {
            const next = d.rows?.find((row) => row.status !== "paid");
            const monthly = d.plan_monthly || d.monthly;
            const collectedPct = Math.max(0, Math.min(100, Math.round((1 - d.balance / (d.start || d.balance || 1)) * 100)));
            return <button key={d.id} type="button" className="finance-receivable-row ds-focusable" onClick={() => st.openDebt(d.id)}>
              <span className="finance-receivable-copy"><strong>{d.name}</strong><span>
                <span className="finance-receivable-term">{d.apr ? +(d.apr / 12).toFixed(2) + "% / mo" : "interest-free"}</span>
                <span>{monthly ? paymentPeso(monthly) + " / mo collection" : "no schedule set"}</span>
                {(next?.due || d.next) && <span>· {next?.due || d.next}</span>}
              </span></span>
              <span className="finance-receivable-balance"><span className="finance-receivable-progress" aria-hidden="true"><i style={{ width: collectedPct + "%", background: d.color || "var(--green)" }} /></span><strong>{paymentPeso(d.balance)}</strong><i className="fa-solid fa-chevron-right" aria-hidden="true" /></span>
            </button>;
          })}
          {closedReceivables.map((d) => <button key={d.id} type="button" className="finance-receivable-row ds-focusable" onClick={() => st.openDebt(d.id)}><span className="finance-receivable-copy"><strong>{d.name}</strong><span>Collected in full</span></span><i className="fa-solid fa-chevron-right" aria-hidden="true" /></button>)}
          <p className="finance-receivables-note">Expected collections are incoming money. Choose a destination account when recording a received payment to update its balance.</p>
        </section>}
        {closedDebts.length > 0 && (
          <div style={{ marginTop: 19 }}>
            <Button variant="quiet" size="compact" className="finance-standing-toggle" onClick={() => setClosedOpen((value) => !value)} aria-expanded={closedOpen} aria-controls={closedLoansId}>
              <span style={{ fontFamily: "var(--mono)", fontSize: 11, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--mut)" }}>Closed loans<span style={{ marginLeft: 8, color: "var(--faint)" }}>{closedDebts.length}</span></span>
              <i className={`fa-solid fa-chevron-${closedOpen ? "up" : "down"}`} aria-hidden="true" style={{ marginLeft: "auto", fontSize: 14, color: "var(--mut)" }} />
            </Button>
            <div id={closedLoansId} hidden={!closedOpen}>
              {closedDebts.map((d) => (
                <button key={d.id} type="button" className="ds-focusable" onClick={() => st.openDebt(d.id)} style={{ display: "flex", alignItems: "center", gap: 13, flexWrap: "wrap", padding: "13px 2px", minHeight: 44, width: "100%", textAlign: "left", color: "var(--ink)", background: "transparent", border: "none", borderTop: "1px solid var(--line)", cursor: "pointer" }}>
                  <span style={{ flex: "1 1 160px", minWidth: 0, fontSize: 13, fontWeight: 700, overflowWrap: "anywhere" }}>{d.name}</span>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: "var(--green-ink)" }}>Paid off</span>
                  <i className="fa-solid fa-chevron-right" aria-hidden="true" style={{ fontSize: 12, color: "var(--mut)", flex: "0 0 auto" }} />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <div style={cardBox}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap", marginBottom: 16 }}>
          <div style={{ fontFamily: "var(--mono)", fontSize: 11, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--mut)" }}>Payment history</div>
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap", fontSize: 11.5, fontWeight: 700, color: "var(--mut)" }}>
            <span>paid <b style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "var(--ink)" }}>{paymentPeso(paidTotal)}</b></span>
            <span>principal recorded <b style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "var(--green-ink)" }}>{paymentPeso(paidPrin)}</b></span>
            <span>interest recorded <b style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "var(--orange-ink)" }}>{paymentPeso(paidInt)}</b></span>
            {unsplitTotal > 0 && <span>Breakdown not recorded <b style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "var(--sub)" }}>{paymentPeso(unsplitTotal)}</b></span>}
          </div>
        </div>
        <div id={paymentHistoryId}>
          {paymentRows.map((p) => {
            const split = completeSplit(p);
            return (
              <div key={p.historyKey} style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", padding: "13px 0", borderTop: "1px solid var(--line)" }}>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: "var(--mut)", minWidth: 52, flex: "0 0 auto" }}>{p.date}</span>
                <div style={{ fontSize: 13, fontWeight: 700, flex: "1 1 150px", minWidth: 0 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <b aria-hidden="true" style={{ width: 6, height: 6, flex: "0 0 auto", borderRadius: 3, background: p.color }} />
                    {p.debtId ? <button type="button" className="ds-focusable" onClick={() => st.openDebt(p.debtId!)} style={{ minHeight: 44, background: "transparent", border: "none", padding: 0, color: "inherit", font: "inherit", textAlign: "left", overflowWrap: "anywhere", cursor: "pointer" }}>{p.name}</button> : <span style={{ overflowWrap: "anywhere" }}>{p.name}</span>}
                  </span>
                  {(p.account || p.receipt) && <span style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "4px 13px", fontSize: 11.5, fontWeight: 600, color: "var(--mut)" }}>
                    {p.account && <span>from {accName(p.account)}</span>}
                    {p.receipt && <a className="ds-focusable" href={p.receipt} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", minHeight: 44, color: "var(--accent-ink)" }} aria-label={`View receipt for ${p.name} on ${p.date}`}>View receipt</a>}
                  </span>}
                </div>
                <span style={{ display: "flex", alignItems: "center", gap: 9, flex: "0 0 auto" }}>
                  {split && <span aria-hidden="true" style={{ display: "block", width: 78, height: 6, borderRadius: 999, background: "var(--warn-line)", overflow: "hidden" }}>
                    <i style={{ display: "block", height: "100%", borderRadius: 999, background: p.color, width: Math.round((p.principal! / p.amount) * 100) + "%" }} />
                  </span>}
                  <span style={{ fontSize: 11, fontWeight: 700, color: !split ? "var(--mut)" : p.interest ? "var(--orange-ink)" : "var(--green-ink)", minWidth: 104 }}>{!split ? "Breakdown not recorded" : p.interest ? paymentPeso(p.interest) + " interest" : "all principal"}</span>
                </span>
                <span style={{ fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums", fontSize: 14, fontWeight: 700, minWidth: 78, textAlign: "right", flex: "0 0 auto" }}>{paymentPeso(p.amount)}</span>
              </div>
            );
          })}
          {paymentRows.length === 0 && <p style={{ fontSize: 13, color: "var(--sub)" }}>No debt payments recorded yet.</p>}
        </div>
        {paymentHistory.length > PAYMENT_PAGE && <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", paddingTop: 13, borderTop: "1px solid var(--line)" }}>
          <span role="status" aria-live="polite" aria-atomic="true" style={{ fontFamily: "var(--mono)", fontSize: 11, fontWeight: 700, color: "var(--mut)" }}>Page {paymentPage + 1} of {paymentPages} · {paymentHistory.length} payments</span>
          <div className="finance-pager">
            <Button size="compact" icon="fa-solid fa-chevron-left" aria-label="Previous payment history page" aria-controls={paymentHistoryId} disabled={paymentPage === 0} onClick={() => setPaymentP(paymentPage - 1)}>Prev</Button>
            <Button size="compact" icon="fa-solid fa-chevron-right" aria-label="Next payment history page" aria-controls={paymentHistoryId} disabled={paymentPage >= paymentPages - 1} onClick={() => setPaymentP(paymentPage + 1)}>Next</Button>
          </div>
        </div>}
        <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--mut)", marginTop: 14 }}>Principal and interest totals include recorded values only. Split bars appear only when the complete breakdown is recorded.</div>
      </div>
    </>
  );

  const hasDebt = st.debts.length > 0 || st.payments.length > 0;
  // Three questions, three tabs. The page had grown to four unrelated jobs in
  // one scroll, and the debt section is by far the longest, so anything above it
  // stopped being found: "where are my expenses" was asked of a page that had a
  // ledger on it the whole time.
  const TABS: [FinTab, string, string][] = [
    ["charts", "Dashboard", "fa-solid fa-chart-pie"],
    ["overview", "Accounts & bills", "fa-solid fa-wallet"],
    ["flow", "Money in & out", "fa-solid fa-right-left"],
    ["debt", "Debt", "fa-solid fa-fire-flame-curved"],
    ["plan", "Plan", "fa-solid fa-route"],
  ];

  return (
    <div className="finance-module">
      <Tabs items={TABS.filter(([key]) => hasDebt || (key !== "debt" && key !== "plan")).map(([id, label, icon]) => ({ id, label: <span className="finance-tab-label"><i className={icon} aria-hidden="true" />{label}</span> }))} value={tab} onChange={(id) => {
        const next = TABS.find(([key]) => key === id)?.[0];
        if (!next) return;
        setTab(next);
        const url = new URL(location.href);
        url.searchParams.set("view", next);
        history.replaceState(null, "", url.pathname + url.search);
      }} label="Finances sections" panelId={sectionsId} />
      <div id={sectionsId} className="finance-section" role="tabpanel" aria-label={TABS.find(([id]) => id === tab)?.[1] || "Overview"}>
        {tab === "charts" && <FinanceAnalytics />}
        {tab === "overview" && glance}
        {tab === "overview" && overview}
        {tab === "flow" && flow}
        {tab === "debt" && hasDebt && debt}
        {tab === "plan" && hasDebt && plan}
      </div>
    </div>
  );
}
